import React, { useEffect, useState } from "react";
import "./FloatingContact.css";

const initialFormData = {
  fullName: "",
  mobile: "",
  loanAmount: "",
  city: "",
};

export default function FloatingContact() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && showForm && !isSubmitting) {
        closeContactForm();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showForm, isSubmitting]);

  useEffect(() => {
    if (showForm) {
      document.body.classList.add("contact-drawer-open");
    } else {
      document.body.classList.remove("contact-drawer-open");
    }

    return () => {
      document.body.classList.remove("contact-drawer-open");
    };
  }, [showForm]);

  const openContactForm = () => {
    setErrors({});
    setStatusMessage("");
    setShowForm(true);
  };

  const closeContactForm = () => {
    if (isSubmitting) return;

    setShowForm(false);
    setErrors({});
    setStatusMessage("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    let cleanedValue = value;

    if (name === "mobile") {
      cleanedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "loanAmount") {
      cleanedValue = value.replace(/\D/g, "");
    }

    setFormData((previousData) => ({
      ...previousData,
      [name]: cleanedValue,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      [name]: "",
    }));

    setStatusMessage("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Please enter your full name.";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Name must contain at least 3 characters.";
    }

    if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit Indian mobile number.";
    }

    const loanAmount = Number(formData.loanAmount);

    if (!formData.loanAmount) {
      newErrors.loanAmount = "Please enter the required loan amount.";
    } else if (Number.isNaN(loanAmount) || loanAmount < 100000) {
      newErrors.loanAmount = "Minimum loan amount is ₹1,00,000.";
    }

    if (!formData.city.trim()) {
      newErrors.city = "Please enter your city.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatusMessage("");

    try {
      const response = await fetch(
        "https://mortbuzz.in/api/instant_submit.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(formData).toString(),
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = await response.text();

      if (result.toLowerCase().includes("success")) {
        setStatusMessage(
          "Thank you! A Monayra loan expert will contact you shortly."
        );

        setFormData(initialFormData);
        setErrors({});

        window.setTimeout(() => {
          setShowForm(false);
          setStatusMessage("");
        }, 2200);
      } else {
        setStatusMessage(
          "We could not submit your request. Please try again."
        );
      }
    } catch (error) {
      console.error("Instant contact form submission error:", error);

      setStatusMessage(
        "The server is currently unavailable. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="floating-contact-btn"
        onClick={openContactForm}
        aria-label="Open instant contact form"
        aria-expanded={showForm}
        aria-controls="instant-contact-drawer"
      >
        <span className="floating-contact-icon">
          <i className="bi bi-telephone-fill"></i>
        </span>

        <span className="floating-contact-text">
          Instant Contact
        </span>
      </button>

      {showForm && (
        <>
          <button
            type="button"
            className="contact-drawer-backdrop"
            onClick={closeContactForm}
            aria-label="Close instant contact form"
          />

          <aside
            id="instant-contact-drawer"
            className="contact-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-drawer-title"
          >
            <div className="contact-drawer-header">
              <div className="contact-drawer-heading">
                <span className="contact-drawer-heading-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <div>
                  <span className="contact-drawer-eyebrow">
                    Quick Assistance
                  </span>

                  <h2 id="contact-drawer-title">
                    Request an Instant Call
                  </h2>
                </div>
              </div>

              <button
                type="button"
                className="contact-drawer-close"
                onClick={closeContactForm}
                aria-label="Close instant contact form"
                disabled={isSubmitting}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="contact-drawer-body">
              <p className="contact-drawer-intro">
                Share your details and a Monayra loan expert will contact
                you shortly.
              </p>

              <form
                className="contact-drawer-form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="contact-form-group">
                  <label htmlFor="instant-full-name">
                    Full Name
                  </label>

                  <div
                    className={`contact-input-wrapper ${
                      errors.fullName ? "has-error" : ""
                    }`}
                  >
                    <i className="bi bi-person"></i>

                    <input
                      id="instant-full-name"
                      type="text"
                      name="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleChange}
                      autoComplete="name"
                      disabled={isSubmitting}
                    />
                  </div>

                  {errors.fullName && (
                    <span className="contact-field-error">
                      {errors.fullName}
                    </span>
                  )}
                </div>

                <div className="contact-form-group">
                  <label htmlFor="instant-mobile">
                    Mobile Number
                  </label>

                  <div
                    className={`contact-input-wrapper ${
                      errors.mobile ? "has-error" : ""
                    }`}
                  >
                    <i className="bi bi-phone"></i>

                    <input
                      id="instant-mobile"
                      type="tel"
                      name="mobile"
                      placeholder="Enter 10-digit mobile number"
                      value={formData.mobile}
                      onChange={handleChange}
                      inputMode="numeric"
                      maxLength={10}
                      autoComplete="tel"
                      disabled={isSubmitting}
                    />
                  </div>

                  {errors.mobile && (
                    <span className="contact-field-error">
                      {errors.mobile}
                    </span>
                  )}
                </div>

                <div className="contact-form-group">
                  <label htmlFor="instant-loan-amount">
                    Required Loan Amount
                  </label>

                  <div
                    className={`contact-input-wrapper ${
                      errors.loanAmount ? "has-error" : ""
                    }`}
                  >
                    <span className="contact-currency-symbol">
                      ₹
                    </span>

                    <input
                      id="instant-loan-amount"
                      type="text"
                      name="loanAmount"
                      placeholder="Example: 5000000"
                      value={formData.loanAmount}
                      onChange={handleChange}
                      inputMode="numeric"
                      disabled={isSubmitting}
                    />
                  </div>

                  {errors.loanAmount && (
                    <span className="contact-field-error">
                      {errors.loanAmount}
                    </span>
                  )}
                </div>

                <div className="contact-form-group">
                  <label htmlFor="instant-city">
                    City
                  </label>

                  <div
                    className={`contact-input-wrapper ${
                      errors.city ? "has-error" : ""
                    }`}
                  >
                    <i className="bi bi-geo-alt"></i>

                    <input
                      id="instant-city"
                      type="text"
                      name="city"
                      placeholder="Enter your city"
                      value={formData.city}
                      onChange={handleChange}
                      autoComplete="address-level2"
                      disabled={isSubmitting}
                    />
                  </div>

                  {errors.city && (
                    <span className="contact-field-error">
                      {errors.city}
                    </span>
                  )}
                </div>

                {statusMessage && (
                  <div
                    className={`contact-status-message ${
                      statusMessage.startsWith("Thank")
                        ? "success"
                        : "error"
                    }`}
                    role="status"
                  >
                    <i
                      className={
                        statusMessage.startsWith("Thank")
                          ? "bi bi-check-circle"
                          : "bi bi-exclamation-circle"
                      }
                    ></i>

                    <span>{statusMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="contact-submit-spinner"
                        aria-hidden="true"
                      ></span>
                      Submitting Request
                    </>
                  ) : (
                    <>
                      Request a Call
                      <i className="bi bi-arrow-right"></i>
                    </>
                  )}
                </button>
              </form>

              <div className="contact-drawer-footer">
                <div>
                  <i className="bi bi-shield-check"></i>
                  Your information is securely submitted
                </div>

                <small>
                  By submitting, you consent to receive a callback regarding
                  your loan enquiry.
                </small>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
}